<!-- 奖励管理 -->
<template>
  <div class="incentive" :class="{ selection }">
    <!-- 查询条件 -->
    <vab-query-form>
      <vab-query-form-left-panel v-if="!selection" :span="4">
        <el-button icon="plus" type="primary" @click="incentiveFormModal.open()">添加奖励</el-button>
      </vab-query-form-left-panel>
      <vab-query-form-right-panel :span="selection ? 24 : 20">
        <el-form inline @submit.prevent="query(1)">
          <el-form-item label="奖励项目">
            <el-input v-model="queryParams.likeName" clearable placeholder="请输入奖励项目" @change="query(1)" />
          </el-form-item>
          <el-form-item label="奖励类型">
            <el-select v-model="queryParams.ruleType" clearable placeholder="请选择奖励类型" @change="query(1)">
              <el-option v-for="item in controlOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button icon="search" type="primary" @click="query()">查询</el-button>
            <el-button icon="refresh" type="warning" @click="reset">重置</el-button>
          </el-form-item>
        </el-form>
      </vab-query-form-right-panel>
    </vab-query-form>

    <!-- 表格 -->
    <el-table
      ref="tableRef"
      v-loading="loading"
      border
      class="hidden-select-all"
      :data="tableData"
      :table-layout="'auto'"
      @select="handleSelect"
    >
      <el-table-column v-if="selection" align="center" :selectable="selectable" type="selection" width="55" />
      <el-table-column label="奖励项目" prop="name" />
      <el-table-column label="类型">
        <template #default="{ row }">
          {{ RULE_TYPE_CN[row.ruleType] }}
        </template>
      </el-table-column>
      <el-table-column :formatter="ruleFormatter" label="条件" />
      <el-table-column label="奖励金额" prop="rewardAmount">
        <template #default="{ row }">{{ row.rewardAmount }}元</template>
      </el-table-column>
      <el-table-column label="有效期" :width="210">
        <template #default="{ row }">{{ timeString(row) }}</template>
      </el-table-column>
      <el-table-column v-if="!selection" label="状态">
        <template #default="{ row }">{{ STATUS_CN[row.status] }}</template>
      </el-table-column>
      <el-table-column v-if="!selection" align="center" label="操作" :width="160">
        <template #default="{ row }">
          <el-link :disabled="isUsing(row)" type="primary" :underline="false" @click="statusModal.open(row)">
            更改状态
          </el-link>
          <el-link
            :disabled="isCumulative(row) || isUsing(row)"
            type="primary"
            :underline="false"
            @click="incentiveFormModal.open(row)"
          >
            编辑
          </el-link>
          <el-link
            :disabled="(isCumulative(row) && !isClosed(row)) || isUsing(row)"
            type="danger"
            :underline="false"
            @click="removeReward(row.id)"
          >
            删除
          </el-link>
        </template>
      </el-table-column>
      <template #empty>
        <el-empty class="vab-data-empty" description="暂无数据" />
      </template>
    </el-table>

    <!-- 分页 -->
    <el-pagination
      v-model:current-page="queryParams.pageNum"
      background
      layout="prev, pager, next"
      :page-size="queryParams.pageSize"
      :total="queryParams.total"
      @current-change="query"
    />

    <!-- 更改状态 -->
    <el-dialog
      v-model="statusModal.visible"
      destroy-on-close
      title="更改状态"
      :width="480"
      @close="statusModal.close()"
    >
      <el-radio-group v-if="statusModal.data" v-model="statusModal.data.status">
        <el-radio v-for="item in statusOptions" :key="item.value" :label="item.value">
          {{ item.label }}
        </el-radio>
      </el-radio-group>
      <template #footer>
        <el-button @click="statusModal.close()">取消</el-button>
        <el-button type="primary" @click="confirmStatus">确定</el-button>
      </template>
    </el-dialog>

    <!-- 编辑奖励 -->
    <el-dialog
      destroy-on-close
      :model-value="incentiveFormModal.visible"
      :title="`${incentiveFormModal.data.id ? '编辑' : '添加'}奖励`"
      :width="680"
      @close="incentiveFormModal.close()"
    >
      <IncentiveForm ref="incentiveFormRef" :data="incentiveFormModal.data" @success="onSuccess" />
      <template #footer>
        <el-button @click="incentiveFormModal.close()">取消</el-button>
        <el-button type="primary" @click="incentiveFormRef.submit()">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
  import { ref, reactive, onMounted, getCurrentInstance, defineProps, toRaw, computed, nextTick } from 'vue'
  import IncentiveForm from './IncentiveForm.vue'
  import { getRewardList, getJobRelationRewardList, updateReward, delReward } from '@/api/recruitment'
  import { useModal } from '@/hooks/useModal'
  import { omit } from 'lodash'
  import { ruleFormatter } from '../helper'
  import dayjs from 'dayjs'

  const { proxy } = getCurrentInstance()

  const tableRef = ref()

  const props = defineProps({
    selection: Boolean,
    rewardRules: {
      type: Array,
      default: () => [],
    },
  })

  const emit = defineEmits(['selection-change'])

  const STATUS_CN = {
    ENABLED: '启用',
    IN_USE: '使用中',
    CLOSED: '下架',
  }

  const statusOptions = [
    { label: '启用', value: 'ENABLED' },
    { label: '下架', value: 'CLOSED' },
  ]

  const RULE_TYPE_CN = {
    CUMULATIVE: '推荐人数',
    NORMAL: '入职时长',
    URGENT: '急招',
  }

  const controlOptions = [
    { label: '入职时长', value: 'NORMAL' },
    { label: '急招', value: 'URGENT' },
    { label: '累计推荐', value: 'CUMULATIVE' },
  ]

  if (props.selection) controlOptions.length = 2

  const defaultQueryParams = {
    likeName: '',
    ruleType: '',
    pageNum: 1,
    pageSize: props.selection ? 10 : 20,
    total: 0,
  }
  const queryParams = reactive({ ...defaultQueryParams })

  const loading = ref(false)
  const tableData = ref([])

  onMounted(() => {
    query()
  })

  function query(pageNum = queryParams.pageNum) {
    const params = {
      ...omit(queryParams, 'total'),
      pageNum,
    }

    loading.value = true

    const getList = props.selection ? getJobRelationRewardList : getRewardList

    getList(params)
      .then(({ code, data }) => {
        if (code !== 200) return
        const { list, total } = data
        tableData.value = list
        queryParams.total = total

        if (props.selection) nextTick(resetSelectionStateOnTable)
      })
      .finally(() => {
        loading.value = false
      })
  }

  function resetSelectionStateOnTable() {
    const targetList = tableData.value.filter((v) =>
      [...lockList.value, ...additionalRewards.value.map((e) => e.id)].includes(v.id)
    )
    targetList.forEach((e) => tableRef.value.toggleRowSelection(e, true))
  }

  function reset() {
    for (const k in defaultQueryParams) {
      queryParams[k] = defaultQueryParams[k]
    }
    query(1)
  }

  function timeString({ timeIsVerified, effectiveFrom, effectiveTo }) {
    if (timeIsVerified) {
      return '长期有效'
    } else {
      return `${dayjs(effectiveFrom).format('YYYY-MM-DD')} 至 ${dayjs(effectiveTo).format('YYYY-MM-DD')}`
    }
  }

  const isUsing = ({ status }) => status === 'IN_USE'
  const isClosed = ({ status }) => status === 'CLOSED'
  const isCumulative = ({ ruleType }) => ruleType === 'CUMULATIVE'

  function onSuccess(type) {
    if (type === 'add') query(1)
    else query()
    incentiveFormModal.close()
  }

  function removeReward(id) {
    proxy.$baseConfirm('你确定要删除当前项吗', null, async () => {
      await delReward(id)
      proxy.$baseMessage(`操作成功！`, 'success', 'vab-hey-message-success')
      query()
    })
  }

  const incentiveFormRef = ref()
  const incentiveFormModal = useModal()

  const statusModal = useModal()

  async function confirmStatus() {
    const params = toRaw(statusModal.data)
    await updateReward(params)
    proxy.$baseMessage(`操作成功！`, 'success', 'vab-hey-message-success')
    statusModal.close()
    query()
  }

  const lockList = computed(() => props.rewardRules.filter((e) => e.locked).map((e) => e.id))
  const additionalRewards = ref(props.rewardRules.filter((e) => !e.locked))

  function selectable(row) {
    const locked = lockList.value.includes(row.id)
    return !locked
  }
  function handleSelect(selection, row) {
    const selected = selection.includes(row)
    if (selected) {
      additionalRewards.value.push(row)
    } else {
      additionalRewards.value = additionalRewards.value.filter((e) => e.id !== row.id)
    }
    emit('additional-change', additionalRewards.value)
  }
</script>

<style lang="scss" scoped>
  .incentive:not(.selection) {
    padding: 16px;
  }

  .hidden-select-all :deep(thead > tr > th.el-table-column--selection > .cell) {
    display: none;
  }
</style>
